import { useEffect, useState, useRef, useImperativeHandle, forwardRef } from "react";
import { toast } from "sonner";
import { Upload, X, Loader2, FileText } from "lucide-react";
import { uploadLibraryDoc, updateLibraryDoc, openProtectedFile, createLibraryLink } from "@/lib/api";
import { errorMessage } from "@/lib/errors";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";

/**
 * Chip input — free-text tags that also suggest from a known list.
 * Press Enter or comma to commit a chip. Used for calibers + tags.
 *
 * Exposes a `flush()` method via ref so the parent form can synchronously
 * commit any pending draft before submitting (otherwise users who type a
 * value and click Save without pressing Enter first would lose the input).
 */
const ChipInput = forwardRef(function ChipInput(
  { value = [], onChange, suggestions = [], placeholder, testid }, ref,
) {
  const [draft, setDraft] = useState("");
  const [showSug, setShowSug] = useState(false);
  const inputRef = useRef(null);
  // Track current value + draft via refs so flush() can read the latest
  // state synchronously even if React hasn't flushed pending updates yet.
  const valueRef = useRef(value);
  valueRef.current = value;
  const draftRef = useRef("");
  draftRef.current = draft;

  const commit = (raw) => {
    const v = (raw ?? draftRef.current ?? "").trim();
    if (!v) return;
    if (valueRef.current.map(String).includes(v)) { setDraft(""); return; }
    onChange([...valueRef.current, v]);
    setDraft("");
  };

  // Imperative flush — called by parent right before submit. Returns the
  // committed array so the caller doesn't have to wait for React state.
  useImperativeHandle(ref, () => ({
    flush() {
      const v = (draftRef.current || "").trim();
      if (!v) return valueRef.current;
      if (valueRef.current.map(String).includes(v)) {
        setDraft("");
        return valueRef.current;
      }
      const next = [...valueRef.current, v];
      onChange(next);
      setDraft("");
      return next;
    },
  }), [onChange]);

  const remove = (idx) => onChange(value.filter((_, i) => i !== idx));

  // Show every existing suggestion the moment the field is focused (so the
  // user can see what collections / tags already exist without typing).
  // Once they start typing, narrow to fuzzy matches.
  const filtered = suggestions
    .map(String)
    .filter((s) => !value.map(String).includes(s) && (!draft || s.toLowerCase().includes(draft.toLowerCase())))
    .slice(0, 12);

  return (
    <div className="relative" data-testid={testid}>
      <div
        className="min-h-[40px] flex flex-wrap gap-1.5 items-center px-2 py-1.5 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm focus-within:border-[#D4AF37]"
        onClick={() => inputRef.current?.focus()}
      >
        {value.map((v, i) => (
          <span
            key={`${v}-${i}`}
            className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#D4AF37]/20 text-[#0A0604] border border-[#D4AF37]/40 rounded-sm text-xs font-mono-data"
            data-testid={`${testid}-chip-${v}`}
          >
            {v}
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); remove(i); }}
              className="text-[#3A2A10] hover:text-rose-500"
              aria-label={`Remove ${v}`}
            >
              <X size={11} />
            </button>
          </span>
        ))}
        <input
          ref={inputRef}
          value={draft}
          onChange={(e) => { setDraft(e.target.value); setShowSug(true); }}
          onFocus={() => setShowSug(true)}
          onBlur={() => {
            // Commit any unfinished draft when focus leaves the field. This
            // is the most common "lost data" failure mode — users type a
            // collection name and click Save without pressing Enter first,
            // so the draft never makes it into `value`. The 180ms delay
            // gives suggestion clicks priority (their onMouseDown commits
            // and resets `draft` to "" before this fires, making the
            // commit() below a no-op).
            setTimeout(() => {
              setShowSug(false);
              if (draft.trim()) commit();
            }, 180);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === ",") {
              e.preventDefault();
              commit();
            } else if (e.key === "Backspace" && !draft && value.length) {
              remove(value.length - 1);
            }
          }}
          placeholder={value.length === 0 ? placeholder : ""}
          className="flex-1 min-w-[120px] bg-transparent text-sm text-[#0A0604] placeholder:text-[#3A2A10]/60 focus:outline-none"
          data-testid={`${testid}-input`}
        />
      </div>
      {showSug && filtered.length > 0 && (
        <div className="absolute z-40 mt-1 w-full max-h-[220px] overflow-y-auto bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm shadow-lg">
          {filtered.map((s) => (
            <button
              key={s}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); commit(s); }}
              className="block w-full text-left px-3 py-2 text-sm text-[#3A2A10] hover:bg-[#2B2214]/[0.06]"
              data-testid={`${testid}-suggest-${s}`}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
});

/**
 * Sheet form for uploading a new document or editing an existing entry.
 * - On create: file is required.
 * - On edit: metadata-only (title, category, manufacturer, calibers, tags, notes).
 * Calibers & tags use chip inputs with suggestions from the movements DB.
 */
export default function LibraryFormSheet({
  open, onOpenChange, doc, categories = [], validation = {},
  defaultCalibers = [], defaultWatchIds = [], defaultManufacturer = "",
  existingCollections = [],
  onSaved,
}) {
  const isEdit = Boolean(doc?.id);
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [calibers, setCalibers] = useState([]);
  const [tags, setTags] = useState([]);
  const [collections, setCollections] = useState([]);
  const [notes, setNotes] = useState("");
  const [watchIds, setWatchIds] = useState([]);
  const [file, setFile] = useState(null);
  // entryType: 'file' = upload a file, 'link' = bookmark with URL only.
  // Defaults to 'file' on create but flips to 'link' for existing link docs.
  const [entryType, setEntryType] = useState("file");
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  // Refs to chip inputs so we can flush any pending draft text into the
  // committed array right before submit. Without this, users who type a
  // collection / caliber / tag and click Save without pressing Enter
  // first will lose that text — a recurring source of "I entered it but
  // it didn't save" reports.
  const calibersRef = useRef(null);
  const collectionsRef = useRef(null);
  const tagsRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (doc) {
      setTitle(doc.title || "");
      setCategory(doc.category || "");
      setManufacturer(doc.manufacturer || "");
      setCalibers(doc.calibers || []);
      setTags(doc.tags || []);
      setCollections(doc.collections || []);
      setNotes(doc.notes || "");
      setWatchIds(doc.watch_ids || []);
      setFile(null);
      setLinkUrl(doc.url || "");
      setEntryType(doc.url ? "link" : "file");
    } else {
      setTitle("");
      setCategory(categories[0] || "Tech Sheet");
      setManufacturer(defaultManufacturer || "");
      setCalibers(defaultCalibers || []);
      setTags([]);
      setCollections([]);
      setNotes("");
      setWatchIds(defaultWatchIds || []);
      setFile(null);
      setLinkUrl("");
      setEntryType("file");
    }
    setProgress(0);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, doc?.id]);

  const onSubmit = async () => {
    if (!title.trim()) {
      toast.error("Title is required");
      return;
    }
    if (!isEdit && entryType === "file" && !file) {
      toast.error("Please choose a file (or switch to Link mode)");
      return;
    }
    if (!isEdit && entryType === "link" && !linkUrl.trim()) {
      toast.error("URL is required for a link entry");
      return;
    }
    // Flush any uncommitted chip drafts (text the user typed but didn't
    // press Enter on). flush() returns the committed array synchronously
    // so we don't have to wait for React state to settle before submit.
    const committedCalibers = calibersRef.current?.flush?.() ?? calibers;
    const committedCollections = collectionsRef.current?.flush?.() ?? collections;
    const committedTags = tagsRef.current?.flush?.() ?? tags;
    setBusy(true);
    try {
      if (isEdit) {
        await updateLibraryDoc(doc.id, {
          title: title.trim(),
          category: category.trim() || "Other",
          manufacturer: manufacturer.trim() || null,
          calibers: committedCalibers,
          watch_ids: watchIds,
          collections: committedCollections,
          tags: committedTags,
          notes: notes.trim() || null,
          url: doc.url ? (linkUrl.trim() || null) : undefined,
        });
        toast.success("Document updated");
      } else if (entryType === "link") {
        await createLibraryLink({
          url: linkUrl.trim(),
          title: title.trim(),
          category: category.trim() || "General Reference",
          manufacturer: manufacturer.trim(),
          calibers: committedCalibers,
          watch_ids: watchIds,
          collections: committedCollections,
          tags: committedTags,
          notes: notes.trim(),
        });
        toast.success("Link saved");
      } else {
        await uploadLibraryDoc(file, {
          title: title.trim(),
          category: category.trim() || "Other",
          manufacturer: manufacturer.trim(),
          calibers: committedCalibers,
          watch_ids: watchIds,
          collections: committedCollections,
          tags: committedTags,
          notes: notes.trim(),
        }, (p) => setProgress(p));
        toast.success("Document uploaded");
      }
      onSaved?.();
    } catch (err) {
      toast.error(errorMessage(err, isEdit ? "Update failed" : "Upload failed"));
    } finally {
      setBusy(false);
      setProgress(0);
    }
  };

  const calibList = validation["Caliber"] || [];
  const mfgList = validation["Mvmnt MFG"] || [];

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        className="w-full sm:max-w-[600px] bg-[#FBF3D9] border-l border-[#8B6A1F]/35 overflow-y-auto"
        data-testid="library-form-sheet"
      >
        <SheetHeader className="mb-6">
          <SheetTitle className="font-serif-display text-3xl text-[#0A0604]">
            {isEdit
              ? (doc?.url ? "Edit Link" : "Edit Document")
              : (entryType === "link" ? "Save a Link" : "Upload Document")}
          </SheetTitle>
          <SheetDescription className="text-[#3A2A10]">
            {isEdit
              ? "Update metadata. The file/URL itself stays the same — delete and recreate to replace."
              : entryType === "link"
                ? "Bookmark a how-to, forum thread, or reference page. Group it into a Collection so it shows up alongside related items."
                : "Attach a PDF, image, or catalog file. Link it to calibers, watches, or suppliers so it surfaces on the matching detail pages."}
          </SheetDescription>
        </SheetHeader>

        <div className="space-y-5">
          {!isEdit && (
            <div className="flex rounded-sm border border-[#8B6A1F]/45 overflow-hidden" data-testid="library-form-type-toggle">
              <button
                type="button"
                onClick={() => setEntryType("file")}
                className={`flex-1 py-2 text-[10px] uppercase tracking-[0.2em] ${entryType === "file" ? "bg-[#D4AF37] text-[#0A0604]" : "bg-[#F9F0D4] text-[#3A2A10] hover:bg-[#F4E9C9]"}`}
                data-testid="library-form-type-file"
              >
                Upload File
              </button>
              <button
                type="button"
                onClick={() => setEntryType("link")}
                className={`flex-1 py-2 text-[10px] uppercase tracking-[0.2em] ${entryType === "link" ? "bg-[#D4AF37] text-[#0A0604]" : "bg-[#F9F0D4] text-[#3A2A10] hover:bg-[#F4E9C9]"}`}
                data-testid="library-form-type-link"
              >
                Save a Link
              </button>
            </div>
          )}

          {!isEdit && entryType === "link" && (
            <div>
              <Label>URL *</Label>
              <input
                type="url"
                value={linkUrl}
                onChange={(e) => {
                  setLinkUrl(e.target.value);
                  // auto-guess title from the URL host
                  if (!title && e.target.value.startsWith("http")) {
                    try {
                      setTitle(new URL(e.target.value).hostname.replace(/^www\./, ""));
                    } catch { /* ignore */ }
                  }
                }}
                placeholder="https://..."
                className="mt-1 w-full h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37]"
                data-testid="library-form-link-url"
              />
            </div>
          )}

          {!isEdit && entryType === "file" && (
            <div>
              <Label>File *</Label>
              <label
                className="mt-1 flex items-center justify-center gap-3 h-20 bg-[#F9F0D4] border-2 border-dashed border-[#8B6A1F]/45 rounded-sm cursor-pointer hover:bg-[#F4E9C9] transition-colors"
                data-testid="library-form-file-drop"
              >
                <input
                  type="file"
                  // Empty value so the same file can be picked twice in a row
                  // (otherwise iOS/iPad onChange doesn't fire on re-select).
                  value=""
                  accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.heic,.heif,.xlsx,.xls,.docx,.doc,.pptx,.ppt,.csv,.txt,.zip"
                  onChange={(e) => {
                    const f = e.target.files?.[0];
                    if (f) {
                      setFile(f);
                      if (!title) setTitle(f.name.replace(/\.[^.]+$/, ""));
                    }
                  }}
                  className="hidden"
                />
                {file ? (
                  <div className="flex items-center gap-3 px-4">
                    <FileText size={20} className="text-[#3A2A10]" />
                    <div>
                      <div className="text-sm text-[#0A0604]">{file.name}</div>
                      <div className="text-[10px] text-[#3A2A10]">{(file.size / 1024).toFixed(0)} KB — click to change</div>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-[#3A2A10]">
                    <Upload size={16} /> Click to choose a file
                  </div>
                )}
              </label>
            </div>
          )}
          {isEdit && doc?.storage_path && (
            <div className="bg-[#F9F0D4] border border-[#8B6A1F]/35 rounded-sm px-4 py-3 flex items-center gap-3 text-sm">
              <FileText size={16} className="text-[#3A2A10]" />
              <div className="flex-1 min-w-0">
                <div className="text-[#0A0604] truncate">{doc.original_filename}</div>
                <div className="text-[10px] text-[#3A2A10]">{(doc.size / 1024).toFixed(0)} KB · {doc.ext?.toUpperCase()}</div>
              </div>
              <button
                type="button"
                onClick={async () => {
                  try {
                    await openProtectedFile(doc.storage_path, doc.original_filename);
                  } catch (err) {
                    toast.error(errorMessage(err, "Could not open document"));
                  }
                }}
                className="text-[11px] uppercase tracking-wider text-[#D4AF37] hover:text-[#E5C158]"
              >
                Open
              </button>
            </div>
          )}

          <div>
            <Label>Title *</Label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. AS 1950/51 Tech Sheet"
              className="mt-1 w-full h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37]"
              data-testid="library-form-title"
            />
          </div>

          <div className={`grid gap-3 ${entryType === "link" ? "grid-cols-1" : "grid-cols-2"}`}>
            <div>
              <Label>Category</Label>
              <input
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                list="library-form-cats"
                className="mt-1 w-full h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37]"
                data-testid="library-form-category"
              />
              <datalist id="library-form-cats">
                {categories.map((c) => <option key={c} value={c} />)}
              </datalist>
            </div>
            {entryType !== "link" && (
              <div>
                <Label>Manufacturer</Label>
                <input
                  value={manufacturer}
                  onChange={(e) => setManufacturer(e.target.value)}
                  list="library-form-mfgs"
                  placeholder="e.g. AS, ETA, BestFit"
                  className="mt-1 w-full h-10 px-3 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37]"
                  data-testid="library-form-mfg"
                />
                <datalist id="library-form-mfgs">
                  {mfgList.map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
            )}
          </div>

          {entryType !== "link" && (
            <div>
              <Label>Calibers covered</Label>
              <div className="text-[10px] text-[#3A2A10] mb-1.5">
                A single doc can cover multiple variants. Type a caliber and press Enter.
              </div>
              <ChipInput
                ref={calibersRef}
                value={calibers}
                onChange={setCalibers}
                suggestions={calibList}
                placeholder="e.g. 1950, 1951, 1952"
                testid="library-form-calibers"
              />
            </div>
          )}

          <div>
            <Label>Collections</Label>
            <div className="text-[10px] text-[#3A2A10] mb-1.5">
              {existingCollections.length > 0
                ? <>Tap the field to pick from your existing collections, or type a new name and press Enter.</>
                : <>Workflow groupings (e.g. "Balance Work", "Shock System Revival"). Type a name and press Enter.</>}
            </div>
            <ChipInput
              ref={collectionsRef}
              value={collections}
              onChange={setCollections}
              suggestions={existingCollections}
              placeholder={existingCollections.length > 0 ? "Tap to see existing…" : "Balance Work, Restoration Videos…"}
              testid="library-form-collections"
            />
          </div>

          <div>
            <Label>Tags</Label>
            <ChipInput
              ref={tagsRef}
              value={tags}
              onChange={setTags}
              suggestions={["chronograph", "automatic", "manual wind", "shock protection", "calendar", "vintage"]}
              placeholder="chronograph, automatic…"
              testid="library-form-tags"
            />
          </div>

          <div>
            <Label>Notes</Label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="Source, version, page references, anything worth remembering…"
              className="mt-1 w-full px-3 py-2 bg-[#F9F0D4] border border-[#8B6A1F]/45 rounded-sm text-sm text-[#0A0604] focus:outline-none focus:border-[#D4AF37] resize-none"
              data-testid="library-form-notes"
            />
          </div>

          {!isEdit && busy && progress > 0 && progress < 100 && (
            <div className="text-[11px] text-[#3A2A10] font-mono-data">Uploading… {progress}%</div>
          )}

          <div className="flex gap-3 pt-2">
            <Button
              onClick={() => onOpenChange(false)}
              variant="outline"
              className="flex-1 bg-transparent border-[#8B6A1F]/45 hover:bg-[#2B2214]/[0.06] text-[#3A2A10] rounded-sm"
              data-testid="library-form-cancel"
            >
              Cancel
            </Button>
            <Button
              onClick={onSubmit}
              disabled={busy}
              className="flex-1 bg-[#D4AF37] hover:bg-[#E5C158] text-[#0A0C10] text-xs uppercase tracking-wider rounded-sm disabled:opacity-50"
              data-testid="library-form-submit"
            >
              {busy ? (
                <><Loader2 size={14} className="mr-2 animate-spin" />
                  {isEdit ? "Saving…" : (entryType === "link" ? "Saving…" : "Uploading…")}
                </>
              ) : (
                isEdit
                  ? "Save Changes"
                  : entryType === "link" ? "Save Link" : "Upload Document"
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Label({ children }) {
  return <div className="text-[10px] uppercase tracking-[0.25em] text-[#3A2A10]">{children}</div>;
}
